// Import de conteúdos via Excel (SheetJS via CDN, lazy load)

import type { Content, ContentType } from '@app-types/domain';
import { parseDurationValue } from '@core/duration';

const SHEETJS_URL = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';

interface XLSXLib {
  read: (data: ArrayBuffer, opts: { type: string }) => XLSXWorkbook;
  utils: {
    sheet_to_json: <T>(
      sheet: XLSXSheet,
      opts?: { header?: number | string[]; defval?: string | number },
    ) => T[];
  };
}
interface XLSXWorkbook {
  SheetNames: string[];
  Sheets: { [name: string]: XLSXSheet };
}
type XLSXSheet = Record<string, unknown>;

declare global {
  interface Window {
    XLSX?: XLSXLib;
  }
}

let xlsxPromise: Promise<XLSXLib> | null = null;
function loadXLSX(): Promise<XLSXLib> {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SHEETJS_URL;
    script.onload = () => {
      if (window.XLSX) resolve(window.XLSX);
      else reject(new Error('XLSX not loaded'));
    };
    script.onerror = () => reject(new Error('Failed to load SheetJS'));
    document.head.appendChild(script);
  });
  return xlsxPromise;
}

type ContentDraft = Omit<Content, 'id' | 'created_at' | 'updated_at' | 'order'>;

export interface ImportPreview {
  drafts: ContentDraft[];
  errors: string[];
  detected_columns: string[];
}

// Aceita colunas comuns: titulo/nome/aula, duracao/duração/minutos, tipo, dificuldade, ordem
function normalizeKey(k: string): string {
  return k
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .replace(/[^a-z0-9]/g, '');
}

function coerceType(v: unknown): ContentType {
  if (typeof v !== 'string') return 'other';
  const n = v.toLowerCase();
  if (n.includes('video') || n.includes('aula')) return 'video';
  if (n.includes('leit') || n.includes('livro') || n.includes('pdf')) return 'reading';
  if (n.includes('exerc') || n.includes('quest') || n.includes('prat')) return 'exercise';
  if (n.includes('revis')) return 'review';
  return 'other';
}
function coerceNumber(v: unknown, def = 0): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number.parseFloat(v.replace(',', '.'));
    return Number.isNaN(n) ? def : n;
  }
  return def;
}
function coerceDifficulty(v: unknown): 1 | 2 | 3 | 4 | 5 {
  const n = Math.round(coerceNumber(v, 3));
  return Math.min(5, Math.max(1, n)) as 1 | 2 | 3 | 4 | 5;
}

export async function importExcelFile(file: File, goalId?: string): Promise<ImportPreview> {
  const xlsx = await loadXLSX();
  const buffer = await file.arrayBuffer();
  const wb = xlsx.read(buffer, { type: 'array' });
  const firstSheet = wb.Sheets[wb.SheetNames[0]];
  if (!firstSheet) {
    return { drafts: [], errors: ['Planilha vazia'], detected_columns: [] };
  }
  const rows = xlsx.utils.sheet_to_json<Record<string, unknown>>(firstSheet, { defval: '' });
  if (rows.length === 0) {
    return { drafts: [], errors: ['Sem linhas de dados'], detected_columns: [] };
  }

  const detected = Object.keys(rows[0]);
  const drafts: ContentDraft[] = [];
  const errors: string[] = [];

  rows.forEach((row, idx) => {
    // Normalize keys of this row
    const norm: Record<string, unknown> = {};
    Object.entries(row).forEach(([k, v]) => {
      norm[normalizeKey(k)] = v;
    });

    const title =
      (norm.titulo as string) ||
      (norm.nome as string) ||
      (norm.aula as string) ||
      (norm.conteudo as string) ||
      '';
    if (!title || !String(title).trim()) {
      errors.push(`Linha ${idx + 2}: sem título`);
      return;
    }

    const duration_min = parseDurationValue(
      norm.duracao ??
        norm.duracaomin ??
        norm.minutos ??
        norm.tempomin ??
        norm.duration ??
        norm.tempo,
      30,
    );

    drafts.push({
      goalId,
      title: String(title).trim(),
      type: coerceType(norm.tipo || norm.categoria || norm.type),
      duration_min,
      difficulty: coerceDifficulty(norm.dificuldade || norm.difficulty || 3),
      status: 'todo',
      source:
        (norm.fonte as string) || (norm.curso as string) || (norm.source as string) || undefined,
    });
  });

  return { drafts, errors, detected_columns: detected };
}

// CSV alternativa (sem SheetJS)
export function importCSVText(text: string, goalId?: string): ImportPreview {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2)
    return { drafts: [], errors: ['CSV precisa de header + linhas'], detected_columns: [] };

  const header = lines[0].split(/[,;\t]/).map((h) => h.replace(/^"|"$/g, '').trim());
  const drafts: ContentDraft[] = [];
  const errors: string[] = [];

  lines.slice(1).forEach((line, i) => {
    const cols = line.split(/[,;\t]/).map((c) => c.replace(/^"|"$/g, '').trim());
    const norm: Record<string, unknown> = {};
    header.forEach((h, idx) => {
      norm[normalizeKey(h)] = cols[idx] || '';
    });
    const title = (norm.titulo as string) || (norm.nome as string) || (norm.aula as string) || '';
    if (!title) {
      errors.push(`Linha ${i + 2}: sem título`);
      return;
    }
    drafts.push({
      goalId,
      title: String(title).trim(),
      type: coerceType(norm.tipo),
      duration_min: parseDurationValue(norm.duracao ?? norm.minutos ?? norm.tempo, 30),
      difficulty: coerceDifficulty(norm.dificuldade || 3),
      status: 'todo',
      source: (norm.fonte as string) || undefined,
    });
  });

  return { drafts, errors, detected_columns: header };
}
